import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getAllNotices } from '../redux/noticeRelated/noticeHandle';
import {
    Paper, Typography, Button, Box, Alert, Card,
    CardContent, Avatar, Stack, Divider, Fade, Chip,
    CircularProgress, IconButton
} from '@mui/material';
import {
    NotificationsActive as NoticeIcon,
    Refresh as RefreshIcon,
    Event as EventIcon,
    ArrowForwardIos as ArrowIcon,
    Campaign as CampaignIcon,
    NewReleases as NewIcon
} from '@mui/icons-material';
import { formatDatePart, getMonthNameFromDate } from '../utils/customDate';
import styled, { keyframes } from 'styled-components';

const fadeIn = keyframes`from { opacity: 0; transform: translateX(20px); } to { opacity: 1; transform: translateX(0); }`;

const NoticeCard = styled(Card)`
    border-radius: 16px !important;
    border: 1px solid #f1f5f9 !important;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05) !important;
    transition: all 0.3s ease !important;
    cursor: pointer;
    background: white !important;
    margin-bottom: 16px;
    animation: ${fadeIn} 0.5s ease-out;
    
    &:hover {
        transform: scale(1.02);
        box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1) !important;
        border-color: #6366f1 !important;
        
        .arrow-icon {
            transform: translateX(4px);
            color: #6366f1;
        }
    }
`;

const DateBadge = styled(Box)`
    padding: 4px 12px;
    border-radius: 8px;
    background: #eef2ff;
    color: #6366f1;
    font-size: 0.7rem;
    font-weight: 800;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
`;

const SeeNotice = () => {
    const dispatch = useDispatch();
    const [refreshKey, setRefreshKey] = useState(0);

    const { currentUser, currentRole } = useSelector((state) => state.user);
    const { userDetails: parentUserDetails } = useSelector((state) => state.parent);
    const { noticesList = [], loading, error, response } = useSelector((state) => state.notice);

    const schoolId = useMemo(() => {
        if (currentRole === 'Admin' && currentUser?._id) return currentUser._id;
        if (currentUser?.school) return typeof currentUser.school === 'object' ? currentUser.school._id : currentUser.school;
        if (parentUserDetails?.school) return typeof parentUserDetails.school === 'object' ? parentUserDetails.school._id : parentUserDetails.school;
        return null;
    }, [currentUser, currentRole, parentUserDetails]);

    const fetchNotices = useCallback(() => {
        if (schoolId) {
            dispatch(getAllNotices(schoolId, 'Notice'));
        }
    }, [dispatch, schoolId]);

    useEffect(() => {
        fetchNotices();
    }, [fetchNotices, refreshKey]);

    const handleRefresh = () => setRefreshKey(prev => prev + 1);

    const showError = error || (response && typeof response === 'string' && response !== 'No notices found');

    if (loading) {
        return (
            <Box sx={{ py: 8, textAlign: 'center' }}>
                <CircularProgress size={30} sx={{ color: '#6366f1' }} />
                <Typography variant="body2" color="textSecondary" sx={{ mt: 2, fontWeight: 600 }}>Fetching latest announcements...</Typography>
            </Box>
        );
    }

    if (!loading && noticesList.length === 0 && !showError) {
        return (
            <Box sx={{ p: 4, textAlign: 'center' }}>
                <Avatar sx={{ bgcolor: '#f1f5f9', color: '#94a3b8', width: 64, height: 64, margin: '0 auto 16px' }}>
                    <CampaignIcon fontSize="large" />
                </Avatar>
                <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b' }}>All Quiet Here</Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>No new notices have been posted yet.</Typography>
                <Button variant="outlined" size="small" onClick={handleRefresh} startIcon={<RefreshIcon />} sx={{ borderRadius: '10px' }}>Refresh</Button>
            </Box>
        );
    }

    if (showError) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="info" sx={{ borderRadius: '12px', mb: 2 }}>
                    {typeof error === 'string' ? error : typeof response === 'string' ? response : 'No notices found'}
                </Alert>
                <Button fullWidth variant="contained" onClick={handleRefresh} sx={{ borderRadius: '12px', bgcolor: '#6366f1' }}>Try Refresh</Button>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, px: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 900, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 1 }}>
                    Recent Feed
                </Typography>
                <IconButton size="small" onClick={handleRefresh} sx={{ bgcolor: '#f8fafc' }}>
                    <RefreshIcon fontSize="small" />
                </IconButton>
            </Box>

            <Stack spacing={0.5}>
                {noticesList.slice(0, 5).map((notice, index) => {
                    const formattedDate = formatDatePart(notice.date);

                    return (
                        <Fade in timeout={400 + index * 100} key={notice._id}>
                            <NoticeCard elevation={0}>
                                <CardContent sx={{ p: '20px !important' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                                        <DateBadge>
                                            <EventIcon sx={{ fontSize: 14 }} /> {formattedDate}
                                        </DateBadge>
                                        {index === 0 && (
                                            <Chip label="NEW" size="small" sx={{ height: 20, fontSize: '0.65rem', fontWeight: 900, bgcolor: '#fef2f2', color: '#ef4444' }} />
                                        )}
                                    </Box>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#1e293b', mb: 0.5, lineHeight: 1.3 }}>
                                        {notice.title}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary" sx={{
                                        display: '-webkit-box',
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: 'vertical',
                                        overflow: 'hidden',
                                        lineHeight: 1.5,
                                        mb: 1
                                    }}>
                                        {notice.details}
                                    </Typography>
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                        <ArrowIcon className="arrow-icon" sx={{ fontSize: 14, color: '#cbd5e1', transition: 'all 0.3s ease' }} />
                                    </Box>
                                </CardContent>
                            </NoticeCard>
                        </Fade>
                    );
                })}
            </Stack>

            {noticesList.length > 5 && (
                <Button fullWidth sx={{ mt: 1, fontWeight: 700, color: '#64748b', textTransform: 'none' }}>
                    View All Announcements
                </Button>
            )}
        </Box>
    );
};

export default SeeNotice;
